/**
 * A window over the portal that leaves it live: the moderator's private call
 * and voice monitor. The portal's counterpart of the game client's
 * FloatingWindow (game-client/src/components/ui/floating-window.tsx).
 *
 * It is not modal. It renders above the app, beside the dialogs (abovePage),
 * on a rung over every dialog's scrim (--z-live-window): no dialog is made
 * inert under it, and it takes neither the focus nor Escape from the one on
 * top — a window opened from a card stays live, its controls the user's, while
 * the card stays open. It is dragged by its header (use-draggable-window.ts),
 * or moved with the arrow keys from the grip at the header's start, and held
 * inside the screen — as it moves, as it opens, and again when the screen
 * shrinks — as the game client's window is; the button in its header closes
 * it.
 *
 * Two sizes: narrow, 320px wide and up to 600px tall, and wide, 500px and up
 * to 80% of the screen's height. Each keeps the header it was drawn with
 * before the two windows shared this one: a line under the narrow one's in
 * the brand's own colour and a 32px close that grows a little under the
 * pointer, and the page's line under the wide one's with a close padded
 * around its glyph.
 *
 *   <FloatingWindow title={t('voiceMonitor.title')} closeLabel={t('common.close')}
 *                   moveLabel={t('common.moveWindow')} onClose={close} size="wide"
 *                   initialPosition={{ x: 340, y: 20 }}>
 *     …the window's body…
 *   </FloatingWindow>
 */

import { useId, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';

import { abovePage } from './modal-layers';
import { useDraggableWindow, type DraggableWindowInput } from './use-draggable-window';
import styles from './floating-window.module.css';

/** How wide it is, and how tall it may grow. */
const SIZE = {
  narrow: styles.narrow,
  wide: styles.wide,
} as const;

/** A window's width: the call's narrow one, the monitor's wide one — and what sits in each is drawn to it. */
export type FloatingWindowSize = keyof typeof SIZE;

/** One arrow press on the grip moves the window this far, as the game client's. */
const STEP = 16;

/** Each arrow key's move. */
const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-STEP, 0],
  ArrowRight: [STEP, 0],
  ArrowUp: [0, -STEP],
  ArrowDown: [0, STEP],
};

interface FloatingWindowProps extends DraggableWindowInput {
  /** Its name, in its header. */
  title: ReactNode;
  /** The close button's accessible name, in the user's language. */
  closeLabel: string;
  /** The grip's accessible name, in the user's language: "Move the window". */
  moveLabel: string;
  onClose: () => void;
  size?: FloatingWindowSize;
  /**
   * The body, under the header: unpadded, as each window lays out its own. The
   * window is a flex column that hides its overflow, as tall as its body up
   * to its size's height: a part of the body that scrolls takes `flex: 1` and
   * `overflow-y: auto`, and is given the room the header leaves.
   */
  children: ReactNode;
}

export function FloatingWindow({
  title,
  closeLabel,
  moveLabel,
  onClose,
  size = 'narrow',
  initialPosition,
  onPositionChange,
  children,
}: FloatingWindowProps) {
  // Its name, its title: a window over the page, not one of the dialogs.
  const titleId = useId();
  const { position, isDragging, windowRef, dragHandleProps, moveBy } = useDraggableWindow({ initialPosition, onPositionChange });
  const onGripKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const move = ARROWS[event.key];
    if (!move) return;
    event.preventDefault();
    moveBy(move[0], move[1]);
  };
  // Where the drag put it, a value only the runtime has: the sheet places it by these.
  const place = { '--window-x': `${position.x}px`, '--window-y': `${position.y}px` } as CSSProperties;

  return abovePage(
    <div className={styles.layer}>
      <div
        ref={windowRef}
        className={[styles.window, SIZE[size], isDragging ? styles.dragging : ''].filter(Boolean).join(' ')}
        style={place}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
      >
        <div className={styles.header} {...dragHandleProps}>
          {/* Drags the window as the header does, and moves it with the arrow keys. */}
          <button type="button" data-grip className={styles.grip} aria-label={moveLabel} onKeyDown={onGripKey}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="9" cy="6" r="1.6" />
              <circle cx="15" cy="6" r="1.6" />
              <circle cx="9" cy="12" r="1.6" />
              <circle cx="15" cy="12" r="1.6" />
              <circle cx="9" cy="18" r="1.6" />
              <circle cx="15" cy="18" r="1.6" />
            </svg>
          </button>
          <h3 id={titleId} className={styles.title}>
            {title}
          </h3>
          <button type="button" className={styles.close} onClick={onClose} aria-label={closeLabel}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
  );
}
